import { useEffect, useRef, useState } from 'react';
import ApexCharts from 'apexcharts';
import { api } from '@/api/growth/dashboard/api';
import {
    GrowthDashboardLaiCardInfo,
    GrowthDashboardReq,
} from '@/api/growth/dashboard/type';
import Tooltip from '@components/common/Tooltip';

interface GrowthLaiInfoCardProps {
    gfIdx: number;
}

const GrowthLaiInfoCard = ({ gfIdx }: GrowthLaiInfoCardProps) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<ApexCharts | null>(null);
    const [loading, setLoading] = useState(true);
    const [laiInfo, setLaiInfo] = useState<GrowthDashboardLaiCardInfo | null>(
        null,
    );

    // LAI 데이터 조회
    useEffect(() => {
        if (gfIdx) {
            fetchLaiInfo(gfIdx);
        }
    }, [gfIdx]);

    const fetchLaiInfo = async (gfIdx: number) => {
        try {
            setLoading(true);
            const req: GrowthDashboardReq = { gfIdx };
            const response = await api.getLaiCardInfo(req);
            setLaiInfo(response.data);
        } catch (error) {
            console.error('LAI 정보 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    // 차트 렌더링
    useEffect(() => {
        if (!chartRef.current || !laiInfo || laiInfo.list.length === 0) return;

        // 기존 차트 인스턴스 제거
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        // LAI 값 배열 추출
        const laiValues = laiInfo.list.map(item => item.lai);

        const options = {
            series: [
                {
                    name: 'LAI',
                    data: laiValues,
                },
            ],
            chart: {
                type: 'area',
                height: 70,
                sparkline: {
                    enabled: true,
                },
                dropShadow: {
                    enabled: true,
                    enabledOnSeries: undefined,
                    top: 0,
                    left: 0,
                    blur: 3,
                    color: 'rgba(var(--secondary-rgb))',
                    opacity: 0.4,
                },
            },
            stroke: {
                curve: 'smooth', // 곡선
                width: 1,
            },
            fill: {
                type: 'gradient',
                gradient: {
                    opacityFrom: 0.5,
                    opacityTo: 0.2,
                    stops: [0, 60],
                    colorStops: [
                        [
                            {
                                offset: 0,
                                color: 'rgba(var(--secondary-rgb))',
                                opacity: 0.3,
                            },
                            {
                                offset: 60,
                                color: 'rgba(var(--secondary-rgb))',
                                opacity: 0.2,
                            },
                        ],
                    ],
                },
            },
            colors: ['rgba(var(--secondary-rgb))'],
            tooltip: {
                fixed: {
                    enabled: false,
                },
                x: {
                    show: false,
                },
                y: {
                    title: {
                        formatter: function () {
                            return '';
                        },
                    },
                },
            },
        };

        chartInstance.current = new ApexCharts(chartRef.current, options);
        chartInstance.current.render();

        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [laiInfo]);

    // 차이 표시 계산 (증가/감소)
    const getDifferenceDisplay = () => {
        if (!laiInfo || laiInfo.laiDifference === null) return null;

        const diff = laiInfo.laiDifference;
        const isPositive = diff >= 0;
        const icon = isPositive
            ? 'ri-arrow-left-up-line'
            : 'ri-arrow-left-down-line';

        return (
            <span className="badge bg-secondary rounded-pill align-items-center fs-11">
                <i className={`${icon} fs-11`}></i> {Math.abs(diff).toFixed(2)}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="card custom-card overflow-hidden">
                <div className="card-body">
                    <div className="d-flex align-items-center justify-content-center">
                        <div
                            className="spinner-border text-secondary"
                            role="status"
                        >
                            <span className="visually-hidden">로딩중...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!laiInfo) {
        return (
            <div className="card custom-card overflow-hidden">
                <div className="card-body">
                    <div className="text-center text-muted">
                        LAI 데이터를 불러올 수 없습니다
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card custom-card overflow-hidden">
            <div className="">
                <div className="card-body">
                    <div className="d-flex align-items-center w-100 justify-content-between gap-1">
                        <div>
                            <p className="mb-1 text-muted fw-medium">
                                평균 입면적지수
                                <Tooltip
                                    content="LAI(Leaf Area Index, 입면적지수)는 단위 토지면적당 잎의 면적을 나타내는 지표로, 작물의 생육 상태와 광합성 능력을 평가하는 중요한 지표입니다. 값이 클수록 잎이 무성하며 광합성 효율이 높습니다."
                                    customClass="tooltip-dark"
                                    placement="top"
                                >
                                    <i className="ri-question-line" />
                                </Tooltip>
                            </p>
                            <h4 className="mb-0 fw-medium">
                                {laiInfo.recentAvgLai
                                    ? laiInfo.recentAvgLai.toFixed(2)
                                    : '0.00'}
                                <small className="fs-11">m²/m²</small>
                            </h4>
                        </div>
                        <div className="ms-auto text-end">
                            {getDifferenceDisplay()}
                            <div className="text-muted fs-12 mt-1">
                                2주전 대비
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    ref={chartRef}
                    id="employees"
                    style={{
                        marginTop: -20,
                    }}
                ></div>
            </div>
        </div>
    );
};

export default GrowthLaiInfoCard;
